import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AnalystBroNavbar from "../../components/AnalystBroNavbar";
import AnalystBroFooter from "../../components/AnalystBroFooter";
import { getBlogPost, BLOG_POSTS } from "../../data/blogPosts";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Bot,
  Compass,
  ChevronRight,
  Tag,
} from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.subtitle,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Article link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-[#131722] text-white flex flex-col font-sans selection:bg-[#4F8EF7]/30 selection:text-white">
      <AnalystBroNavbar />

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center justify-between">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs font-semibold text-[#4F8EF7] hover:underline"
          >
            <ArrowLeft size={14} />
            <span>Back to All Research</span>
          </Link>

          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium text-[#C2C6D5] transition"
          >
            <Share2 size={13} />
            <span>Share</span>
          </button>
        </div>

        {/* Article Header */}
        <header className="space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#4F8EF7]/15 text-[#4F8EF7] border border-[#4F8EF7]/30">
              {post.category}
            </span>
            <span className="text-xs text-[#C2C6D5]/60 flex items-center gap-1.5">
              <Calendar size={13} /> {post.date}
            </span>
            <span className="text-xs text-[#C2C6D5]/60 flex items-center gap-1.5">
              <Clock size={13} /> {post.readTime}
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            {post.title}
          </h1>

          <p className="text-base sm:text-xl text-[#C2C6D5]/80 leading-relaxed font-light">
            {post.subtitle}
          </p>

          {/* Author Block */}
          <div className="flex items-center gap-3.5 pt-4 border-t border-white/5">
            <img
              src={post.author.avatar}
              alt={post.author.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-white/15"
            />
            <div>
              <div className="font-bold text-sm text-white">{post.author.name}</div>
              <div className="text-xs text-[#C2C6D5]/60">{post.author.role}</div>
            </div>
          </div>
        </header>

        {/* Key Takeaways Callout Box */}
        {post.keyTakeaways && post.keyTakeaways.length > 0 && (
          <div className="rounded-2xl border border-[#4bd9c0]/30 bg-[#4bd9c0]/5 p-6 space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#4bd9c0] flex items-center gap-2">
              <Sparkles size={16} /> Key Takeaways
            </h3>
            <ul className="space-y-2.5">
              {post.keyTakeaways.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#C2C6D5]">
                  <CheckCircle2 size={16} className="text-[#4bd9c0] flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related Tool Callout if present */}
        {post.relatedTool && (
          <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#101e29] to-[#142633] p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#4bd9c0]/20 text-[#4bd9c0] flex items-center justify-center flex-shrink-0">
                <Compass size={20} />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#4bd9c0]">
                  Interactive Companion Tool
                </span>
                <div className="font-bold text-sm text-white">
                  {post.relatedTool.name}
                </div>
              </div>
            </div>
            <Link
              to={post.relatedTool.path}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#4bd9c0] text-[#09131b] font-bold text-xs hover:brightness-110 transition shadow-md shadow-[#4bd9c0]/20 flex-shrink-0"
            >
              <span>{post.relatedTool.cta}</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Article Body (Markdown) */}
        <article className="prose prose-invert max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-h3:text-2xl prose-h4:text-xl prose-p:text-[#C2C6D5] prose-p:leading-relaxed prose-li:text-[#C2C6D5] prose-strong:text-white prose-table:border prose-table:border-white/10 prose-th:bg-white/5 prose-th:p-3 prose-td:p-3 prose-td:border-t prose-td:border-white/5 prose-pre:bg-[#0e131d] prose-pre:border prose-pre:border-white/10 prose-pre:rounded-xl">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </article>

        {/* Tags */}
        <div className="pt-6 border-t border-white/5 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-[#C2C6D5]/60 mr-2 flex items-center gap-1">
            <Tag size={12} /> Tags:
          </span>
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-lg bg-white/5 text-xs text-[#C2C6D5] border border-white/5 font-mono"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* AgentBro Investigation Box */}
        <div className="rounded-3xl border border-[#4F8EF7]/30 bg-gradient-to-r from-[#172033] via-[#1a253c] to-[#172033] p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-2xl">
          <div className="space-y-2 max-w-lg">
            <div className="flex items-center gap-2 text-xs font-bold text-[#4F8EF7]">
              <Bot size={16} /> Autonomous Research Workflow
            </div>
            <h4 className="text-xl font-bold text-white">
              Want deeper analysis on this topic?
            </h4>
            <p className="text-xs sm:text-sm text-[#C2C6D5]/80 leading-relaxed">
              Launch AnalystBro to execute custom multi-factor screens, pull SEC EDGAR filings, and test institutional flow signals in real time.
            </p>
          </div>
          <Link
            to="/agentbro"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#4F8EF7] px-6 py-3.5 text-sm font-bold text-white shadow-xl shadow-[#4F8EF7]/25 hover:brightness-110 active:scale-95 transition flex-shrink-0"
          >
            <Bot size={16} />
            <span>Launch AnalystBro</span>
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <div className="pt-8 space-y-4">
            <h3 className="text-lg font-bold text-white">Related Research</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedPosts.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition group flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-[#4F8EF7] uppercase tracking-wider">
                      {r.category}
                    </span>
                    <h5 className="font-bold text-white text-sm group-hover:text-[#4F8EF7] transition-colors line-clamp-2">
                      {r.title}
                    </h5>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#C2C6D5]/60 pt-4 mt-2 border-t border-white/5">
                    <span>{r.readTime}</span>
                    <span className="text-[#4F8EF7] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-semibold">
                      Read <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <AnalystBroFooter />
    </div>
  );
}
